import { useState, useRef, useEffect, useCallback } from "react";

const COLORS = [
  "#00ff88",
  "#ff3b3b",
  "#4da6ff",
  "#ffffff",
  "#ffd700",
  "#ff69b4",
  "#ff8c00",
  "#c084fc",
  "#34d399",
  "#f97316",
];

const TOOLS = [
  { id: "pen", icon: "✏️", label: "Pen" },
  { id: "marker", icon: "🖊️", label: "Marker" },
  { id: "highlighter", icon: "🖍️", label: "Highlighter" },
  { id: "eraser", icon: "🧹", label: "Eraser" },
  { id: "text", icon: "T", label: "Text" },
  { id: "shape-rect", icon: "▭", label: "Rectangle" },
  { id: "shape-circle", icon: "○", label: "Circle" },
  { id: "shape-line", icon: "╱", label: "Line" },
];

const BG_COLORS = ["#1a1b2e", "#0f172a", "#14140f", "#1c1c2e", "#f0f0e8"];
const NUM_PAGES = 5;

function getPos(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const sx = canvas.width / rect.width;
  const sy = canvas.height / rect.height;
  let cx,
    cy,
    p = 1;
  if (e.touches) {
    const t = e.touches[0] || e.changedTouches[0];
    cx = t.clientX;
    cy = t.clientY;
    p = t.force > 0 ? t.force : 1;
  } else {
    cx = e.clientX;
    cy = e.clientY;
    p = e.pressure > 0 ? e.pressure : 1;
  }
  return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy, p };
}

function TextInput({ x, y, color, size, onCommit, onCancel }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <textarea
      ref={ref}
      autoFocus
      style={{
        position: "absolute",
        left: x,
        top: y,
        minWidth: 140,
        minHeight: 40,
        background: "transparent",
        border: `1.5px dashed ${color}`,
        borderRadius: 4,
        color,
        fontSize: size * 3 + 8,
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        resize: "both",
        padding: "2px 6px",
        zIndex: 100,
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onCommit(e.target.value);
        }
      }}
      onBlur={(e) => onCommit(e.target.value)}
      placeholder="Type here…"
    />
  );
}

export default function Jamboard() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#00ff88");
  const [size, setSize] = useState(4);
  const [bg, setBg] = useState("#1a1b2e");
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(() => Array(NUM_PAGES).fill(null));
  const [undos, setUndos] = useState(() =>
    Array(NUM_PAGES)
      .fill(null)
      .map(() => []),
  );
  const [redos, setRedos] = useState(() =>
    Array(NUM_PAGES)
      .fill(null)
      .map(() => []),
  );
  const [textPos, setTextPos] = useState(null);
  const [shapeStart, setShapeStart] = useState(null);
  const [shapeSnap, setShapeSnap] = useState(null);
  const [sidebar, setSidebar] = useState(true);

  const fillBg = useCallback((ctx, b) => {
    ctx.fillStyle = b;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const resize = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      const tmp = document.createElement("canvas");
      tmp.width = canvas.width;
      tmp.height = canvas.height;
      tmp.getContext("2d").drawImage(canvas, 0, 0);
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      fillBg(ctx, bg);
      if (pages[page]) ctx.putImageData(pages[page], 0, 0);
      else ctx.drawImage(tmp, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();
    return () => ro.disconnect();
  }, [page, bg]); // eslint-disable-line

  const saveCurrent = useCallback((idx) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas
      .getContext("2d")
      .getImageData(0, 0, canvas.width, canvas.height);
    setPages((prev) => {
      const n = [...prev];
      n[idx] = data;
      return n;
    });
  }, []);

  const switchPage = useCallback(
    (np) => {
      saveCurrent(page);
      setPage(np);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      fillBg(ctx, bg);
      setPages((prev) => {
        if (prev[np]) ctx.putImageData(prev[np], 0, 0);
        return prev;
      });
    },
    [page, bg, saveCurrent, fillBg],
  );

  const pushUndo = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const snap = canvas
      .getContext("2d")
      .getImageData(0, 0, canvas.width, canvas.height);
    setUndos((prev) => {
      const n = prev.map((s) => [...s]);
      n[page] = [...n[page].slice(-40), snap];
      return n;
    });
    setRedos((prev) => {
      const n = [...prev];
      n[page] = [];
      return n;
    });
  }, [page]);

  const undo = useCallback(() => {
    setUndos((prev) => {
      const stack = prev[page];
      if (!stack.length) return prev;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const cur = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setRedos((r) => {
        const n = r.map((s) => [...s]);
        n[page] = [...n[page], cur];
        return n;
      });
      ctx.putImageData(stack[stack.length - 1], 0, 0);
      const n = prev.map((s) => [...s]);
      n[page] = n[page].slice(0, -1);
      return n;
    });
  }, [page]);

  const redo = useCallback(() => {
    setRedos((prev) => {
      const stack = prev[page];
      if (!stack.length) return prev;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const cur = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setUndos((u) => {
        const n = u.map((s) => [...s]);
        n[page] = [...n[page], cur];
        return n;
      });
      ctx.putImageData(stack[stack.length - 1], 0, 0);
      const n = prev.map((s) => [...s]);
      n[page] = n[page].slice(0, -1);
      return n;
    });
  }, [page]);

  const applyStroke = useCallback((ctx, from, to, p, t, c, s) => {
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (t === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.lineWidth = s * 4;
      ctx.globalAlpha = 1;
    } else if (t === "marker") {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = c;
      ctx.lineWidth = s * 2.5 * p;
      ctx.globalAlpha = 0.5;
    } else if (t === "highlighter") {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = c;
      ctx.lineWidth = s * 6;
      ctx.globalAlpha = 0.25;
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = c;
      ctx.lineWidth = Math.max(1, s * p);
      ctx.globalAlpha = 1;
    }
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
  }, []);

  const drawShape = useCallback((ctx, snap, from, to, t, c, s) => {
    if (snap) ctx.putImageData(snap, 0, 0);
    ctx.strokeStyle = c;
    ctx.lineWidth = s;
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.lineCap = "round";
    ctx.beginPath();
    if (t === "shape-rect") {
      ctx.strokeRect(from.x, from.y, to.x - from.x, to.y - from.y);
    } else if (t === "shape-circle") {
      const rx = Math.abs(to.x - from.x) / 2,
        ry = Math.abs(to.y - from.y) / 2;
      ctx.ellipse(
        from.x + (to.x - from.x) / 2,
        from.y + (to.y - from.y) / 2,
        rx || 1,
        ry || 1,
        0,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
    } else if (t === "shape-line") {
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
    }
  }, []);

  const onDown = useCallback(
    (e) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch (_) {}
      const pos = getPos(e, canvas);
      if (tool === "text") {
        setTextPos({ x: pos.x, y: pos.y });
        return;
      }
      pushUndo();
      drawing.current = true;
      last.current = pos;
      if (tool.startsWith("shape")) {
        const ctx = canvas.getContext("2d");
        setShapeStart(pos);
        setShapeSnap(ctx.getImageData(0, 0, canvas.width, canvas.height));
      }
    },
    [tool, pushUndo],
  );

  const onMove = useCallback(
    (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const pos = getPos(e, canvas);
      if (tool.startsWith("shape") && shapeStart) {
        drawShape(ctx, shapeSnap, shapeStart, pos, tool, color, size);
      } else {
        applyStroke(ctx, last.current, pos, pos.p, tool, color, size);
        last.current = pos;
      }
    },
    [tool, color, size, shapeStart, shapeSnap, applyStroke, drawShape],
  );

  const onUp = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    if (tool.startsWith("shape")) {
      setShapeStart(null);
      setShapeSnap(null);
    }
  }, [tool]);

  const commitText = useCallback(
    (val) => {
      if (!val.trim()) {
        setTextPos(null);
        return;
      }
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      pushUndo();
      ctx.font = `${size * 3 + 8}px 'Segoe UI', sans-serif`;
      ctx.fillStyle = color;
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      val
        .split("\n")
        .forEach((ln, i) =>
          ctx.fillText(ln, textPos.x, textPos.y + (i + 1) * (size * 3 + 14)),
        );
      setTextPos(null);
    },
    [color, size, textPos, pushUndo],
  );

  const clearPage = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    pushUndo();
    fillBg(ctx, bg);
  }, [bg, fillBg, pushUndo]);

  const saveImg = useCallback(() => {
    const link = document.createElement("a");
    link.download = `jamboard-page${page + 1}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  }, [page]);

  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z") {
          e.preventDefault();
          undo();
        }
        if (e.key === "y") {
          e.preventDefault();
          redo();
        }
        if (e.key === "s") {
          e.preventDefault();
          saveImg();
        }
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [undo, redo, saveImg]);

  const cursorFor = {
    pen: "crosshair",
    marker: "crosshair",
    highlighter: "crosshair",
    eraser: "cell",
    text: "text",
    "shape-rect": "crosshair",
    "shape-circle": "crosshair",
    "shape-line": "crosshair",
  };
  const toolLabel = TOOLS.find((t) => t.id === tool)?.label || "Pen";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        background: "#0d0e1a",
        fontFamily: "'Segoe UI', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* ── Topbar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "5px 14px",
          height: 44,
          flexShrink: 0,
          background: "#0a0b15",
          borderBottom: "1px solid #1e2040",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              color: "#ff4444",
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: 1,
            }}
          >
            DESIQNA
          </span>
          <button
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "none",
              background: "transparent",
              color: "#6c7aad",
              cursor: "pointer",
              fontSize: 16,
            }}
            onClick={() => setSidebar((o) => !o)}
          >
            ☰
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            style={{
              background: "#f5c842",
              border: "none",
              borderRadius: "50%",
              width: 28,
              height: 28,
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 15,
              color: "#1a1200",
            }}
            onClick={() => switchPage((page - 1 + NUM_PAGES) % NUM_PAGES)}
          >
            ←
          </button>
          <span
            style={{
              color: "#f5c842",
              fontSize: 13,
              fontWeight: 600,
              minWidth: 96,
              textAlign: "center",
            }}
          >
            राम राम — {page + 1}/{NUM_PAGES}
          </span>
          <button
            style={{
              background: "#f5c842",
              border: "none",
              borderRadius: "50%",
              width: 28,
              height: 28,
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 15,
              color: "#1a1200",
            }}
            onClick={() => switchPage((page + 1) % NUM_PAGES)}
          >
            →
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {BG_COLORS.map((b) => (
            <div
              key={b}
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: b,
                border: bg === b ? "2px solid #00e5ff" : "2px solid #444",
                cursor: "pointer",
              }}
              onClick={() => {
                setBg(b);
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext("2d");
                const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = b;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.putImageData(img, 0, 0);
              }}
            />
          ))}
          <div
            style={{
              width: 1,
              height: 22,
              background: "#1e2040",
              margin: "0 4px",
            }}
          />
          <button
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              border: "none",
              background: "transparent",
              color: "#6c7aad",
              cursor: "pointer",
              fontSize: 15,
            }}
            onClick={undo}
            title="Undo Ctrl+Z"
          >
            ↩
          </button>
          <button
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              border: "none",
              background: "transparent",
              color: "#6c7aad",
              cursor: "pointer",
              fontSize: 15,
            }}
            onClick={redo}
            title="Redo Ctrl+Y"
          >
            ↪
          </button>
          <button
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              border: "none",
              background: "#c0392b",
              color: "#fff",
              cursor: "pointer",
              fontSize: 15,
            }}
            onClick={clearPage}
            title="Clear"
          >
            🗑
          </button>
          <button
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              border: "none",
              background: "#1a4a2a",
              color: "#00ff88",
              cursor: "pointer",
              fontSize: 15,
            }}
            onClick={saveImg}
            title="Save PNG"
          >
            💾
          </button>
        </div>
      </div>

      {/* ── Main ── */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* ── Sidebar ── */}
        {sidebar && (
          <div
            style={{
              width: 46,
              flexShrink: 0,
              background: "#0a0b15",
              borderRight: "1px solid #1e2040",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "8px 0",
              gap: 3,
              overflowY: "auto",
            }}
          >
            {TOOLS.map((t) => (
              <button
                key={t.id}
                title={t.label}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  flexShrink: 0,
                  border:
                    tool === t.id
                      ? "2px solid #00e5ff"
                      : "2px solid transparent",
                  background: tool === t.id ? "#1a2a40" : "transparent",
                  color: tool === t.id ? "#00e5ff" : "#6c7aad",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: t.id === "text" ? 11 : 13,
                  fontWeight: t.id === "text" ? 700 : 400,
                }}
                onClick={() => setTool(t.id)}
              >
                {t.icon}
              </button>
            ))}

            <div
              style={{
                width: 26,
                height: 1,
                background: "#1e2040",
                margin: "3px 0",
                flexShrink: 0,
              }}
            />

            {COLORS.map((c) => (
              <div
                key={c}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: c,
                  flexShrink: 0,
                  border:
                    color === c
                      ? "2.5px solid #fff"
                      : "2.5px solid transparent",
                  cursor: "pointer",
                  transform: color === c ? "scale(1.18)" : "scale(1)",
                  transition: "transform 0.1s",
                }}
                onClick={() => {
                  setColor(c);
                  if (tool === "eraser") setTool("pen");
                }}
              />
            ))}

            <div
              style={{
                width: 26,
                height: 1,
                background: "#1e2040",
                margin: "3px 0",
                flexShrink: 0,
              }}
            />

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <span
                style={{ color: "#6c7aad", fontSize: 9, letterSpacing: 0.5 }}
              >
                SIZE
              </span>
              <input
                type="range"
                min={1}
                max={40}
                value={size}
                step={1}
                style={{
                  WebkitAppearance: "slider-vertical",
                  appearance: "slider-vertical",
                  width: 20,
                  height: 80,
                  cursor: "pointer",
                  accentColor: "#00e5ff",
                }}
                onChange={(e) => setSize(+e.target.value)}
              />
              <span style={{ color: "#00e5ff", fontSize: 10, fontWeight: 600 }}>
                {size}
              </span>
            </div>
          </div>
        )}

        {/* ── Canvas wrapper ── */}
        <div
          ref={wrapRef}
          style={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            minWidth: 0,
            minHeight: 0,
            cursor: cursorFor[tool] || "crosshair",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              touchAction: "none",
            }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerLeave={onUp}
            onPointerCancel={onUp}
          />

          {textPos && (
            <TextInput
              x={textPos.x}
              y={textPos.y}
              color={color}
              size={size}
              onCommit={commitText}
              onCancel={() => setTextPos(null)}
            />
          )}

          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: 12,
              background: "rgba(255,215,0,0.12)",
              border: "1px solid #ffd70033",
              color: "#ffd700",
              fontSize: 11,
              padding: "3px 10px",
              borderRadius: 20,
              pointerEvents: "none",
            }}
          >
            Page {page + 1} / {NUM_PAGES}
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 12,
              right: 12,
              background: "rgba(0,229,255,0.1)",
              border: "1px solid #00e5ff33",
              color: "#00e5ff",
              fontSize: 11,
              padding: "3px 10px",
              borderRadius: 20,
              pointerEvents: "none",
            }}
          >
            {toolLabel} · {size}px
          </div>
        </div>
      </div>
    </div>
  );
}
